/**
 * Comparaison « développement » d'une saisie (contenu.comparaison =
 * 'developpement', décision prof 2026-09-24). Pour une carte qui fait
 * DÉVELOPPER, aucune liste ne peut tenir toutes les écritures justes
 * (9x×2 + 9×7x², 7×9x², 63x×x…) : le correcteur calcule.
 *
 * La saisie est juste si :
 *   1. elle est ÉGALE au résultat attendu : mêmes coefficients devant chaque
 *      puissance de la lettre, quel que soit l'ordre ou le regroupement ;
 *   2. il n'y reste AUCUNE parenthèse contenant une somme : 9x(2 + 7x) est
 *      égal mais pas développé. Une parenthèse autour d'un seul terme reste
 *      permise (9 × (−3), comme le cours).
 * Une suite d'égalités « 9x(2 + 7x) = 18x + 63x² » passe si chaque membre est
 * égal et que le DERNIER est développé.
 *
 * Lecture stricte, rien n'est deviné : « 63x2 » (x² ou 2x ?), « 9x x 2 » (la
 * lettre n'est jamais le signe ×), « 72x + −27 » et « 9 × −3 » (deux signes à
 * la suite, le cours met des parenthèses), une autre lettre que celle du
 * résultat : illisible, donc faux.
 *
 * Réutilisable par tout gen qui fait développer : makeSaisie(…, { comparaison:
 * 'developpement' }), avec une bonne_reponse développée en KaTeX simple
 * (nombres, une seule lettre, ^2, \times, {,}). Les deux chaînes reçues ici
 * sont déjà normalisées (questionUtils.normaliserExpression : sans espace,
 * * pour ×, ^2 pour ², point décimal, minuscules).
 */

// Au-delà, ce n'est pas une réponse tapée : illisible (et pas de récursion sans fin).
const LONGUEUR_MAX = 200;
const EXPOSANT_MAX = 6;

// ── Polynômes : coefficients rangés par puissance de la lettre ([nombre, x, x², …])
const additionner = (p, q) => Array.from({ length: Math.max(p.length, q.length) }, (_, k) => (p[k] || 0) + (q[k] || 0));
const opposer = (p) => p.map((c) => -c);
const multiplier = (p, q) => {
    const r = new Array(p.length + q.length - 1).fill(0);
    p.forEach((a, i) => q.forEach((b, j) => { r[i + j] += a * b; }));
    return r;
};
const egaux = (p, q) => Array.from({ length: Math.max(p.length, q.length) }, (_, k) => [p[k] || 0, q[k] || 0])
    .every(([a, b]) => Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b)));

/**
 * Lit UN membre (sans « = ») écrit avec la lettre `lettre` : son polynôme, et
 * s'il reste une parenthèse contenant une somme. null si illisible.
 * Produit implicite : devant une parenthèse, et devant la lettre après un
 * nombre ou une parenthèse (9x, 6(…), x(…)), jamais lettre contre lettre
 * (« xx ») ni un nombre collé derrière (« x2 », « )6 »). Un signe ne se
 * place qu'en tête d'un membre ou d'une parenthèse.
 */
const lireMembre = (s, lettre) => {
    let i = 0;
    let parentheseSomme = false;
    let somme = null;

    /** Nombre, lettre ou (somme), puis un exposant entier facultatif : { poly, sorte } ou null. */
    const facteur = () => {
        let poly;
        let sorte;
        const c = s[i];
        if (c === '(') {
            i += 1;
            const dedans = somme();
            if (!dedans || s[i] !== ')') return null;
            i += 1;
            if (dedans.termes > 1) parentheseSomme = true;
            poly = dedans.poly;
            sorte = 'parenthese';
        } else if (c === lettre) {
            i += 1;
            poly = [0, 1];
            sorte = 'lettre';
        } else {
            const m = /^\d+(?:\.\d+)?/.exec(s.slice(i));
            if (!m) return null;
            i += m[0].length;
            poly = [Number(m[0])];
            sorte = 'nombre';
        }
        if (s[i] === '^') {
            const m = /^\d+/.exec(s.slice(i + 1));
            if (!m || Number(m[0]) > EXPOSANT_MAX) return null;
            i += 1 + m[0].length;
            let puissance = [1];
            for (let k = 0; k < Number(m[0]); k++) puissance = multiplier(puissance, poly);
            poly = puissance;
        }
        return { poly, sorte };
    };

    const produit = () => {
        const premier = facteur();
        if (!premier) return null;
        let { poly, sorte } = premier;
        for (;;) {
            if (s[i] === '*') i += 1;
            else if (!(s[i] === '(' || (s[i] === lettre && sorte !== 'lettre'))) break;
            const suivant = facteur();
            if (!suivant) return null;
            poly = multiplier(poly, suivant.poly);
            sorte = suivant.sorte;
        }
        return poly;
    };

    somme = () => {
        const moinsEnTete = s[i] === '-';
        if (s[i] === '+' || s[i] === '-') i += 1;
        const premier = produit();
        if (!premier) return null;
        let poly = moinsEnTete ? opposer(premier) : premier;
        let termes = 1;
        while (s[i] === '+' || s[i] === '-') {
            const moins = s[i] === '-';
            i += 1;
            const terme = produit();
            if (!terme) return null;
            poly = additionner(poly, moins ? opposer(terme) : terme);
            termes += 1;
        }
        return { poly, termes };
    };

    const lu = somme();
    if (!lu || i !== s.length) return null;
    return { poly: lu.poly, parentheseSomme };
};

/**
 * Le KaTeX simple d'une bonne_reponse ramené à ce qu'on tape au clavier
 * (« $\mathrm{}18x + 63x^{2}$ » → « 18x + 63x^2 »), à normaliser ensuite.
 */
export const texteDuLatex = (latex) => String(latex ?? '')
    .replace(/\$/g, '')
    .replace(/\\mathrm\{\}/g, '')
    .replace(/\\(?:times|cdot)/g, '*')
    .replace(/\{,\}/g, ',')
    .replace(/\\left|\\right/g, '')
    .replace(/\\[,;!: ]/g, '')
    .replace(/\^\{(\d+)\}/g, '^$1');

/**
 * La saisie (normalisée) est-elle un développement juste du résultat attendu
 * (normalisé) ? Faux si l'un ou l'autre est illisible, si le résultat attendu
 * n'est pas lui-même développé ou n'a pas exactement une lettre.
 */
export const jugerDeveloppement = (saisie, attendu) => {
    if (!saisie || saisie.length > LONGUEUR_MAX) return false;
    const lettres = new Set(String(attendu).match(/[a-z]/g) || []);
    if (lettres.size !== 1) return false;
    const [lettre] = lettres;
    const reference = lireMembre(String(attendu), lettre);
    if (!reference || reference.parentheseSomme) return false;
    const membres = saisie.split('=').map((m) => lireMembre(m, lettre));
    if (membres.some((m) => !m || !egaux(m.poly, reference.poly))) return false;
    return !membres[membres.length - 1].parentheseSomme;
};
